import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { api } from "../../services/api";
import Swal from "sweetalert2";

export default function ModalImportacao({ show, onHide }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [lookups, setLookups] = useState({ roteamentos: [], bandeiras: [], condicoes: [] });
    const [form, setForm] = useState({ roteamento: '', bandeira: '', condicao: '', taxa: 0 });

    useEffect(() => {
        if (show) {
            api.get('/financeiro/conciliacao/roteamentos')
                .then(res => setLookups(prev => ({ ...prev, roteamentos: res.data })))
                .catch(err => console.error("Erro ao carregar roteamentos", err));
        } else {
            setForm({ roteamento: '', bandeira: '', condicao: '', taxa: 0 });
            setData([]);
        }
    }, [show]);

    // --- FUNÇÃO PARA FORMATAR DATA PARA dd/mm/yyyy ---
    const formatarDataBR = (valor) => {
        if (!valor) return '';
        
        // Se for objeto Date do Excel
        if (valor instanceof Date) {
            const dia = String(valor.getDate()).padStart(2, '0');
            const mes = String(valor.getMonth() + 1).padStart(2, '0');
            const ano = valor.getFullYear();
            return `${dia}/${mes}/${ano}`;
        }

        // Se for uma string (comum em CSV) tenta converter ou limpar
        const s = String(valor).trim();
        if (s.includes('-')) { // Formato yyyy-mm-dd
            const [y, m, d] = s.split('T')[0].split('-');
            return `${d}/${m}/${y}`;
        }
        
        return s; // Retorna o valor original se já estiver ok ou for NSU/etc
    };

    const handleRoteamento = async (val) => {
        setForm({ roteamento: val, bandeira: '', condicao: '', taxa: 0 });
        if (!val) return;
        try {
            const res = await api.get(`/financeiro/conciliacao/bandeiras?roteamento=${val}`);
            setLookups(prev => ({ ...prev, bandeiras: ["Usar do arquivo", ...res.data], condicoes: [] }));
        } catch (err) {
            Swal.fire("Erro", "Erro ao buscar bandeiras", "error");
        }
    };

    const handleBandeira = async (val) => {
        setForm(prev => ({ ...prev, bandeira: val, condicao: '', taxa: 0 }));
        if (!val) return;
        try {
            const res = await api.get(`/financeiro/conciliacao/condicoes?roteamento=${form.roteamento}&bandeira=${val}`);
            setLookups(prev => ({ ...prev, condicoes: res.data }));
        } catch (err) {
            console.error(err);
        }
    };

    const handleCondicao = async (val) => {
        setForm(prev => ({ ...prev, condicao: val }));
        if (!val) return;
        try {
            const res = await api.get(`/financeiro/conciliacao/taxa?roteamento=${form.roteamento}&bandeira=${form.bandeira}&condicao=${val}`);
            setForm(prev => ({ ...prev, condicao: val, taxa: res.data.taxa || 0 }));
        } catch (err) {
            console.error(err);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            // cellDates: true faz o XLSX converter colunas de data em objetos Date do JS
            const wb = XLSX.read(bstr, { type: 'binary', cellDates: true });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const json = XLSX.utils.sheet_to_json(ws);
            setData(json);
        };
        reader.readAsBinaryString(file);
    };

    const handleSincronizar = async () => {
        if (!form.condicao || data.length === 0) {
            Swal.fire("Atenção", "Preencha todos os filtros e selecione o arquivo", "warning");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                taxa: form.taxa,
                itens: data.map(i => ({
                    ROTEAMENTO: form.roteamento,
                    BANDEIRA: form.bandeira,
                    CONDICAO: form.condicao,
                    LOJA: i['Loja'] || i['Descrição Loja'] || '',
                    // Aplicando a formatação dd/mm/yyyy aqui:
                    DATAVENDA: formatarDataBR(i['Data Venda']),
                    DATACREDITO: formatarDataBR(i['Data Crédito']),
                    NUMCOMPROVANTE: i['Número Comprovante'] || '',
                    NSU: i['NSU TEF'] || '',
                    VALORBRUTO: parseFloat(String(i['Valor Bruto'] || 0).replace('"', '').replace(',', '.'))
                }))
            };

            await api.post('/financeiro/conciliacao/importar', payload);
            Swal.fire("Sucesso", "Importação realizada!", "success");
            onHide();
        } catch (err) {
            Swal.fire("Erro", "Falha na importação", "error");
        } finally {
            setLoading(false);
        }
    };

    const renderOptions = (lista, campo) => {
        return lista.map((item, index) => {
            const valor = typeof item === 'object' ? item[campo] : item;
            return <option key={index} value={valor}>{valor}</option>;
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
            <Modal.Header closeButton className="bg-success text-white">
                <Modal.Title className="small fw-bold">IMPORTAÇÃO DE ARQUIVO DE CONCILIAÇÃO</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
                <div className="card p-3 shadow-sm border-0 mb-3">
                    <div className="row g-2">
                        <div className="col-md-3">
                            <label className="small fw-bold">Roteamento</label>
                            <select className="form-select form-select-sm" value={form.roteamento} onChange={e => handleRoteamento(e.target.value)}>
                                <option value="">Selecione...</option>
                                {renderOptions(lookups.roteamentos, 'ROTEAMENTO')}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="small fw-bold">Bandeira</label>
                            <select className="form-select form-select-sm" value={form.bandeira} onChange={e => handleBandeira(e.target.value)}>
                                <option value="">Selecione...</option>
                                {renderOptions(lookups.bandeiras, 'BANDEIRA')}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="small fw-bold">Condição</label>
                            <select className="form-select form-select-sm" value={form.condicao} onChange={e => handleCondicao(e.target.value)}>
                                <option value="">Selecione...</option>
                                {renderOptions(lookups.condicoes, 'CONDICAO')}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="small fw-bold text-danger text-center w-100">Taxa do Roteamento</label>
                            <div className="form-control form-control-sm text-center fw-bold bg-white">
                                {form.taxa.toFixed(2)}%
                            </div>
                        </div>
                    </div>

                    <div className="mt-3">
                        <label className="small fw-bold">Selecionar Arquivo (CSV ou Excel)</label>
                        <input type="file" className="form-control form-control-sm" accept=".csv, .xlsx, .xls" onChange={handleFileUpload} />
                    </div>
                </div>

                <div className="card shadow-sm border-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <table className="table table-sm table-striped table-hover mb-0" style={{ fontSize: '0.7rem' }}>
                        <thead className="table-secondary sticky-top">
                            <tr>
                                {data.length > 0 && Object.keys(data[0]).map(k => <th key={k}>{k}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {data.slice(0, 50).map((row, i) => (
                                <tr key={i}>
                                    {Object.values(row).map((v, j) => <td key={j}>{String(v)}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.length > 50 && <div className="text-center p-2 small text-muted">Exibindo apenas as primeiras 50 linhas...</div>}
                </div>
            </Modal.Body>
            <Modal.Footer className="bg-white">
                <Button variant="secondary" size="sm" onClick={onHide} disabled={loading}>
                    CANCELAR
                </Button>
                <Button variant="success" size="sm" className="fw-bold" onClick={handleSincronizar} disabled={loading || data.length === 0}>
                    {loading ? 'PROCESSANDO...' : 'INICIAR IMPORTAÇÃO'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}